import type { ProductHitVideo } from '@libs/algolia';
import type { AlgoliaRecordInterface } from './inventory.interface';
import type { PersonalizationType } from '../product';

export enum AlgoliaOperations {
    REMOVE = 'Remove',
    ADD = 'AddUnique',
}

export interface Image {
    large: string;
    small: string;
}

export interface LocalizedValue {
    'en-US': string | Date | number;
}

export interface FormattedPrice {
    primary?: string;
    strikethrough?: string;
}

export interface Price {
    number: number | undefined;
    displayType: string;
    formatted: FormattedPrice;
}

export type MerchandisingHierarchy = {
    category: string;
    class: string;
    department: string;
    division: string;
    vicePresidentId: number;
    vicePresidentName: string;
};

export type categoryInfo = {
    id: string | undefined;
    name: {
        default: string;
    };
};
export type categoryListInfo = Array<categoryInfo[]>;

export type metaValue = {
    title: string;
    description: string;
};

export type MasksList = { [key: string]: { us: string[]; ca: string[] } }[];

export interface ProductInterfaceAlgolia extends AlgoliaRecordInterface {
    id: number;
    masterProductID: number;
    name: string;
    brand?: string | null;
    primary_category_id: string;
    primary_category_name: string;
    short_description: string | null;
    long_description?: string | null;
    personalization: PersonalizationType;
    merchandisingHierarchy: MerchandisingHierarchy;
    meta: metaValue;
    categories?: categoryListInfo;
    onlineFrom?: number | null;
    onlineTo?: number | null;
    customBvAverageRating?: number | null;
    bvAverageRating?: number | null;
    bvReviewCount?: number;
    customHealthConsideration?: string[];
    inventory?: Array<number | string>;
    customPet?: string[];
    ordersPlaced?: number | null;
    revenue?: number | null;
    unitsSold?: number | null;
    customCaseQuantity?: number | null;
    maxOrderQty?: number | null;
    size?: { [key: string]: string | string[] } | null;
    color?: { [key: string]: string | string[] } | null;
    flavor?: { [key: string]: string | string[] } | null;
    hasHazmatCode?: boolean | null;
    images: Image;
    price?: Price | null;
    bopisCaseQty?: number | null;
    customPharmacyAutoshipBypass?: boolean | null;
    customPharmacyColdStorage?: string | null;
    customPharmacyOOS?: boolean | null;
    customScheduledDeliveryEligible?: boolean | null;
    discontinued?: boolean | null;
    discontinuedOverride?: boolean | null;
    isCaseRequiredSTH?: boolean | null;
    isCustomizationMandatory?: boolean | null;
    isPoBoxRestricted?: boolean | null;
    isSubscriptionEnabled?: boolean | null;
    statesRestrictionEnabled?: boolean | null;
    postalCodeRestrictionEnabled?: boolean | null;
    sizeChartImgUrl?: string | null;
    variations?: string[];
    moreWaysToShop?: string[];
    package?: { [key: string]: string | string[] } | null;
    dimensionsAndWeight?: { [key: string]: string | string[] } | null;
    'shoppingOptions-isBopisEligible'?: boolean | null;
    video?: ProductHitVideo;
    alternateImages?: string[];
    [key: string]:
        | string
        | string[]
        | number
        | number[]
        | PersonalizationType
        | ProductHitVideo
        | metaValue
        | MerchandisingHierarchy
        | categoryListInfo
        | Price
        | Image
        | boolean
        | Array<string | number>
        | LocalizedValue
        | LocalizedValue[]
        | { [key: string]: string | string[] }
        | { [key: string]: number | string }
        | null
        | undefined;
}

export interface ProductUpdateObject extends AlgoliaRecordInterface {
    sku: number;
    inventory: {
        _operation: AlgoliaOperations;
        value: string | number;
    };
}

export enum merchandisingHierarchyEnum {
    category = 'merchandisingHierarchyCategory',
    class = 'merchandisingHierarchyClass',
    department = 'merchandisingHierarchyDepartment',
    division = 'merchandisingHierarchyDivision',
    vicePresidentId = 'merchandisingHierarchyVicePresidentId',
    vicePresidentName = 'merchandisingHierarchyVicePresidentName',
}

export enum personalizationEnum {
    templateId = 'personalizationTemplateID',
    templateName = 'personalizationTemplateName',
    templateDescription = 'personalizationTemplateDescription',
    customizationType = 'personalizationCustomizationType',
    threadColors = 'personalizationStyleOptionsThreadColors',
    styleFinishes = 'personalizationStyleOptionsStyleFinishes',
    fontFaceOptions = 'personalizationFontFaceOptions',
    frontLines = 'personalizationTextLocationsFrontLines',
    backLines = 'personalizationTextLocationsBackLines',
}

export enum dimensionsAndWeightEnum {
    cartonHeight = 'dimensionsAndWeightCartonHeight',
    cartonLength = 'dimensionsAndWeightCartonLength',
    cartonWeight = 'dimensionsAndWeightCartonWeight',
    cartonWidth = 'dimensionsAndWeightCartonWidth',
}

export enum productPackageEnum {
    packageHeight = 'packageHeight',
    packageLength = 'packageLength',
    packageWeight = 'packageWeight',
    packageWidth = 'packageWidth',
}

export interface productBatchRecords {
    us: ProductInterfaceAlgolia[];
    ca: ProductInterfaceAlgolia[];
}
